import React from 'react';
import * as Sentry from '@sentry/react';
import { Box, Typography, Button, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

class LazyLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Same reason as ErrorBoundary: catching here means React never rethrows, so
    // a failed chunk load is invisible to Sentry unless reported explicitly.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
      tags: { errorBoundary: 'lazyLoad' }
    });

    console.error('Lazy load error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    // Force a page reload to clear any module loading issues
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
          p={3}
        >
          <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Component Loading Error
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              There was an error loading this page component. This might be due to a network issue or a temporary loading problem.
            </Typography>
          </Alert>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={this.handleRetry}
            size="large"
          >
            Retry
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default LazyLoadErrorBoundary;